import { bind, Component, h, tag } from "omi";
import classNames from "classnames";
import every from "lodash-es/every";

import { NWButton } from "@/components/atoms/nw-button";
import { SearchResult, SearchResultEntity } from "@/api";
import { NWBadge, NWIcon, NWTooltip, openModal } from "@/components";
import { Store } from "@/store";
import { tailwind } from "@/tailwind";

import { ListItem } from "../../../components/searchList/listItem";
import { CourseTopic } from "../../../store/models";
import { useProjects } from "../../../store/queries/useProjects";
import { DirectoryStore } from "../../../store";
import { getMetadata } from "../../../utils";

export type CourseSearchResult = Required<SearchResult> & {
  type: "course";
};

type Props = {
  searchResult: CourseSearchResult;
  madeForYou?: boolean;
  recommendedResult: CourseTopic;
  onBack: () => void;
  onGoalEdit?: () => void;
};

@tag("nw-directory-course-view")
export class NWDirectoryCourseView extends Component<Props> {
  static css = [tailwind];

  @bind
  private handleBack() {
    this.props.onBack();
  }

  @bind
  private handleSelection(
    event: MouseEvent,
    entities: SearchResultEntity[],
    selected: boolean,
    seriesTitle: string | undefined,
  ) {
    event.preventDefault();

    if (!Store.user.isLoggedIn.value) {
      return openModal("login", {});
    }

    const ids = entities.map(e => e.id);

    DirectoryStore.updateRoadmap(ids, selected);

    import("@/services/analyticsService").then(({ useAnalyticsService }) => {
      useAnalyticsService().trackDirectoryAddToMyProjectsClicked(
        "series",
        seriesTitle ? [seriesTitle] : ids,
        selected ? "added" : "removed",
      );
    });
  }

  private getProjectProps(entity: SearchResultEntity) {
    return {
      ...entity,
      type: "project",
      description: "",
      title: useProjects().getProject(entity.id)?.metadata?.title || "",
      entities: [entity],
    };
  }

  private getData(recommendedResult: CourseTopic, searchResult: CourseSearchResult) {
    const title = searchResult.title || recommendedResult.value.metadata?.title;
    const description = searchResult.description || recommendedResult.value?.metadata?.description;
    const entities = searchResult.entities || recommendedResult.value.metadata?.projects;
    const imageUrl = searchResult.imageUrl || recommendedResult.value.metadata?.imageUrl;
    const hasEntities = entities?.length > 0;
    const isInRoadmap = hasEntities && every(entities, e => getMetadata(e).selected === "true");

    return { title, description, entities, projectCount: entities.length, isInRoadmap, imageUrl, hasEntities };
  }

  private renderMadeForYouGoal(description: string | undefined) {
    return (
      <NWTooltip
        content="Editing your goal will update your projects"
        placement="top"
        strategy="absolute"
        class="w-full"
      >
        <button
          class="text-sm text-white text-left cursor-pointer w-full flex-1 bg-leather"
          onClick={this.props.onGoalEdit}
        >
          <div className="p-4">
            {description}{" "}
            <span class="inline-block">
              <NWIcon name="pencil" />
            </span>
          </div>
          <div className="absolute inset-0 z-0 bg-no-repeat bg-cover bg-center rounded-b-[20px] bg-leather/90 flex items-center justify-center group-hover:opacity-100 opacity-0 transition-all duration-300 ease-in-out">
            <NWIcon name="pencil" class="text-white mr-1" width={20} height={20} />
            <span class="text-white text-sm font-semibold">Edit goal</span>
          </div>
        </button>
      </NWTooltip>
    );
  }

  render() {
    const { title, description, entities, projectCount, isInRoadmap, imageUrl, hasEntities } = this.getData(
      this.props.recommendedResult,
      this.props.searchResult,
    );

    return (
      <div className={"animate-fadeInFast flex flex-col py-3 transition-all rounded-[20px] space-y-3"}>
        <NWButton type="text" size="fit" icon="chevron-left" onClick={this.handleBack} class="w-fit">
          Back
        </NWButton>
        <div
          className={classNames(
            "card shadow-md border border-brand-200 rounded-2xl flex-1 flex flex-col justify-end items-start relative overflow-hidden bg-leather",
            { "pt-12": this.props.madeForYou },
          )}
        >
          {imageUrl && (
            <>
              <div
                className={classNames("absolute z-0 bg-no-repeat bg-cover bg-leather", {
                  "bottom-1/4 inset-x-0 top-0 bg-bottom": this.props.madeForYou,
                  "inset-0 bg-center": !this.props.madeForYou,
                })}
                style={{
                  backgroundImage: `url('${imageUrl}')`,
                }}
              />
              <div
                className={classNames("absolute bottom-0 left-0 right-0 z-0", {
                  "top-[42%]": this.props.madeForYou,
                  "top-[40%]": !this.props.madeForYou,
                })}
                style={{
                  background: this.props.madeForYou
                    ? "linear-gradient(rgba(0, 0, 0, 0.2) 0%, rgba(27, 25, 24, 0.8) 10%)"
                    : "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(27, 25, 24, 0.80) 76.71%)",
                }}
              />
            </>
          )}
          <div className="card-header flex flex-col items-start pt-12 z-[1] w-full">
            <div className="flex items-end justify-between w-full px-4 pt-4">
              <div className="flex flex-col gap-2">
                <h2 className="text-2xl font-semibold mb-1 text-white text-left">{title}</h2>
                <NWBadge color="gray-filled" padding="xs" class="w-fit">
                  <span className="normal-case">{projectCount} Projects</span>
                </NWBadge>
              </div>
              {hasEntities && (
                <NWTooltip content="Add series to my projects" placement="top" strategy="absolute">
                  <button
                    className={classNames(
                      "transition-all duration-150 rounded-lg ml-4 p-2 shrink-0 h-9 w-9 flex items-center justify-center",
                      {
                        "bg-green-300 hover:bg-green-400 text-white": isInRoadmap,
                        "bg-secondary hover:bg-secondary-hover text-leather": !isInRoadmap,
                      },
                    )}
                    onClick={e => this.handleSelection(e, entities, !isInRoadmap, title)}
                  >
                    <NWIcon
                      name={isInRoadmap ? "checkbox-tick" : "plus"}
                      width={isInRoadmap ? 20 : 16}
                      height={isInRoadmap ? 20 : 16}
                    />
                  </button>
                </NWTooltip>
              )}
            </div>
            <div
              className={classNames("flex items-center gap-2 w-full group relative", {
                "min-h-20": this.props.madeForYou,
                "mt-3": !this.props.madeForYou,
              })}
            >
              {this.props.madeForYou ? (
                this.renderMadeForYouGoal(description)
              ) : (
                <div className="p-4 text-sm text-white text-left">{description}</div>
              )}
            </div>
          </div>
        </div>
        <div className="pt-3 space-y-2 overflow-hidden transition-all duration-300 ease-in-out">
          <div class="flex flex-col gap-2">
            {hasEntities && entities?.map(entity => <ListItem {...this.getProjectProps(entity)} type="project" />)}
            {!hasEntities && (
              <NoResultsForGoalView onClick={this.props.madeForYou ? this.props.onGoalEdit! : this.props.onBack} />
            )}
          </div>
        </div>
      </div>
    );
  }
}

function NoResultsForGoalView({ onClick }: { onClick: () => void }) {
  return (
    <div className="my-6">
      <div className="flex flex-col items-center justify-center p-6 text-center max-w-sm mx-auto">
        <h3 className="font-semibold text-primary">No projects found</h3>
        <p className="text-sm text-tertiary mt-2">
          Your goal does not align with any of our current projects. Please refine your goal and try again.
        </p>
        <NWButton type="primary" size="fit" class="pt-6" onClick={onClick}>
          Refine goal
        </NWButton>
      </div>
    </div>
  );
}
